import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { SearchAddon } from '@xterm/addon-search'
import '@xterm/xterm/css/xterm.css'
import {
  ArrowDown,
  Download,
  Pause,
  Search as SearchIcon,
  X as XIcon,
} from 'lucide-react'
import { observabilityAPI } from '@hpanel/web-common/api/client'
import type {
  TenantObservabilityLogEvent,
} from '@hpanel/web-common/api/types'
import type { SSEStatus } from '@hpanel/web-common/api/client'

/**
 * LogTerminal — live log viewer for one tenant domain.
 *
 * Wraps xterm.js for the terminal surface (scrollback, copy, search,
 * keyboard navigation come for free) and consumes the per-domain SSE log
 * stream via `observabilityAPI.streamLogs`.
 *
 * Behaviors locked in the brief:
 *   - Live ring buffer only — no history persistence client-side; the
 *     ~2000-line scrollback is xterm's own.
 *   - Follow-tail by default; if the user scrolls up the terminal auto-
 *     pauses scroll-to-bottom and exposes a "Resume tail" button.
 *   - Pause button stops appending entirely (stream stays open).
 *   - Search via @xterm/addon-search.
 *   - Download button shells out to the one-shot text endpoint.
 *   - On stream drop: dim `— reconnecting —` status line, no banner. On
 *     terminal degrade (429 or 5x retry): banner with the exact server
 *     detail and the Download button as the manual escape hatch.
 */

interface LogTerminalProps {
  domainId: number
  domainName: string
}

const SCROLLBACK_LINES = 2000

/** ANSI escape helpers — keep stderr visually distinct without a library. */
const ANSI_RED = '\x1b[31m'
const ANSI_RESET = '\x1b[0m'

const formatLine = (entry: TenantObservabilityLogEvent): string => {
  // Strip trailing newlines from the server; xterm needs explicit \r\n.
  const trimmed = entry.line.replace(/[\r\n]+$/, '')
  if (entry.stream === 'stderr') {
    return `${ANSI_RED}${trimmed}${ANSI_RESET}\r\n`
  }
  return `${trimmed}\r\n`
}

export const LogTerminal: React.FC<LogTerminalProps> = ({ domainId, domainName }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const termRef = useRef<Terminal | null>(null)
  const fitRef = useRef<FitAddon | null>(null)
  const searchRef = useRef<SearchAddon | null>(null)
  const handleRef = useRef<{ close: () => void } | null>(null)

  // `followTail` controls whether incoming lines auto-scroll the viewport.
  // It flips off when the user scrolls up; explicitly clicking "Resume tail"
  // (or scrolling back to the bottom) re-enables it.
  const [followTail, setFollowTail] = useState(true)
  // `paused` is user-initiated and stops appending entirely. The SSE stays
  // open in the background; flipping pause off will resume from the next
  // server-emitted line (we intentionally don't buffer paused-while-closed).
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(paused)
  pausedRef.current = paused

  const [status, setStatus] = useState<SSEStatus>('connecting')
  const [degradedDetail, setDegradedDetail] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  // See StatsPanel: server-sent stream errors were console-only. The degraded
  // banner covers the terminal 429/5-failure state, but a one-off 404 or 502
  // left the terminal blank with no explanation.
  const [streamError, setStreamError] = useState<string | null>(null)
  // Visually-hidden per-line mirror so QA / a11y can assert individual log
  // lines. xterm renders its buffer to canvas (or rows that recycle), so we
  // cannot put a stable `observability-log-line` testid on the xterm DOM
  // itself. We keep the last SCROLLBACK_LINES entries here in render-state
  // and let CSS hide the list from sighted users.
  const [visibleLines, setVisibleLines] = useState<TenantObservabilityLogEvent[]>([])

  // Mount xterm once and keep the instance for the lifetime of the panel.
  useEffect(() => {
    if (!containerRef.current) return
    const term = new Terminal({
      cursorBlink: false,
      disableStdin: true,
      scrollback: SCROLLBACK_LINES,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: 12,
      lineHeight: 1.35,
      theme: {
        background: '#0a0a0a',
        foreground: '#e5e7eb',
        cursor: '#0a0a0a',
        selectionBackground: '#3b82f6',
      },
      convertEol: false,
      allowProposedApi: true,
    })
    const fit = new FitAddon()
    const search = new SearchAddon()
    term.loadAddon(fit)
    term.loadAddon(search)
    term.open(containerRef.current)
    try { fit.fit() } catch { /* container may not have laid out yet */ }

    // Detect manual scroll-away from the bottom: xterm reports a viewportY
    // event on every wheel/keystroke scroll. We compare against the buffer
    // base to decide whether the user is "at the bottom" or has scrolled up.
    const onScroll = () => {
      if (!termRef.current) return
      const buf = termRef.current.buffer.active
      const atBottom = buf.viewportY >= buf.baseY
      setFollowTail(atBottom)
    }
    term.onScroll(onScroll)

    termRef.current = term
    fitRef.current = fit
    searchRef.current = search

    // Refit on container resize. ResizeObserver beats window.resize for
    // modal-hosted terminals because the panel can change size without the
    // window doing so.
    const ro = new ResizeObserver(() => {
      try { fit.fit() } catch { /* ignore */ }
    })
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      term.dispose()
      termRef.current = null
      fitRef.current = null
      searchRef.current = null
    }
  }, [])

  // Open the SSE stream. We re-open whenever `domainId` changes.
  useEffect(() => {
    handleRef.current?.close()
    setStatus('connecting')
    setDegradedDetail(null)

    const handle = observabilityAPI.streamLogs(domainId, {
      onLog: (entry) => {
        if (pausedRef.current) return
        const term = termRef.current
        if (!term) return
        term.write(formatLine(entry))
        // If the user is following tail, force scroll-to-bottom. xterm's
        // default is "scroll on new data only when at bottom" — we want to
        // honor `followTail` explicitly so the Resume button works.
        if (followTailRef.current) {
          term.scrollToBottom()
        }
        // Mirror into the per-line list. Cap at SCROLLBACK_LINES so the
        // hidden DOM doesn't grow unbounded.
        setVisibleLines((prev) => {
          const next = prev.length >= SCROLLBACK_LINES
            ? [...prev.slice(prev.length - SCROLLBACK_LINES + 1), entry]
            : [...prev, entry]
          return next
        })
      },
      onError: (message) => {
        // eslint-disable-next-line no-console
        console.error('[observability] log stream server-error:', message)
        setStreamError(message)
      },
      onStatusChange: (next, errorDetail) => {
        setStatus(next)
        if (next === 'degraded' && errorDetail) {
          setDegradedDetail(errorDetail)
        }
      },
    })
    handleRef.current = handle

    return () => {
      handle.close()
      handleRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId])

  // Mirror followTail into a ref so the SSE onLog closure (created once per
  // domainId, not per render) can read the latest value without resubscribing.
  const followTailRef = useRef(followTail)
  followTailRef.current = followTail

  const handleResumeTail = useCallback(() => {
    setFollowTail(true)
    termRef.current?.scrollToBottom()
  }, [])

  const handleTogglePause = useCallback(() => {
    setPaused((p) => !p)
  }, [])

  const handleDownload = useCallback(async () => {
    setDownloading(true)
    setDownloadError(null)
    try {
      await observabilityAPI.downloadLogs(domainId, 2000)
    } catch (err: unknown) {
      setDownloadError(err instanceof Error ? err.message : 'Failed to download logs')
    } finally {
      setDownloading(false)
    }
  }, [domainId])

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!searchRef.current || !searchValue) return
      searchRef.current.findNext(searchValue, { incremental: false })
    },
    [searchValue],
  )

  // Status line text — dim and ephemeral, never blocks the terminal.
  const statusText = useMemo(() => {
    if (status === 'connecting') return '— connecting —'
    if (status === 'reconnecting') return '— reconnecting —'
    return null
  }, [status])

  const isDegraded = status === 'degraded'

  return (
    <div
      className="flex flex-col h-full min-h-0"
      data-testid="observability-log-terminal"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="text-[12px] font-medium text-gray-700 dark:text-gray-200 truncate"
            title={domainName}
          >
            Logs · {domainName}
          </span>
          {statusText && (
            <span
              data-testid="observability-log-status"
              className="text-[11px] text-gray-400 dark:text-gray-500 italic"
            >
              {statusText}
            </span>
          )}
          {status === 'open' && (
            <span className="text-[11px] text-green-600 dark:text-green-400">live</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setSearchOpen((s) => !s)}
            className="h-7 inline-flex items-center gap-1 px-2 rounded-md border border-gray-200 dark:border-gray-800 text-[11px] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Search logs"
            data-testid="observability-log-search-toggle"
          >
            <SearchIcon className="h-3 w-3" strokeWidth={1.75} />
            Search
          </button>
          <button
            type="button"
            onClick={handleTogglePause}
            className="h-7 inline-flex items-center gap-1 px-2 rounded-md border border-gray-200 dark:border-gray-800 text-[11px] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label={paused ? 'Resume appending' : 'Pause appending'}
            data-testid="observability-log-pause"
          >
            <Pause className="h-3 w-3" strokeWidth={1.75} />
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="h-7 inline-flex items-center gap-1 px-2 rounded-md border border-gray-200 dark:border-gray-800 text-[11px] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            aria-label="Download last 2000 lines"
            data-testid="observability-download-button"
          >
            <Download className="h-3 w-3" strokeWidth={1.75} />
            {downloading ? 'Downloading…' : 'Download'}
          </button>
        </div>
      </div>

      {/* Search bar — toggled by search button */}
      {searchOpen && (
        <form
          onSubmit={handleSearchSubmit}
          className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900"
        >
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Find in logs"
            data-testid="observability-log-search-input"
            className="flex-1 h-7 px-2 rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="submit"
            className="h-7 px-2 rounded-md border border-gray-200 dark:border-gray-800 text-[11px] text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Find
          </button>
          <button
            type="button"
            onClick={() => {
              setSearchOpen(false)
              setSearchValue('')
              searchRef.current?.clearDecorations()
            }}
            className="h-7 w-7 grid place-items-center rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close search"
          >
            <XIcon className="h-3 w-3" strokeWidth={1.75} />
          </button>
        </form>
      )}

      {/* Degraded banner (only when terminal-state) */}
      {isDegraded && degradedDetail && (
        <div
          data-testid="observability-degraded-banner"
          className="px-3 py-2 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-[12px] text-amber-800 dark:text-amber-200"
        >
          {degradedDetail}
        </div>
      )}

      {/*
        Non-terminal stream errors — a 404 for a domain the tenant does not own,
        a 502 from the proxy — which never reach the degraded state and so were
        invisible: the terminal simply stayed empty. Suppressed once degraded
        takes over so the two banners don't stack saying the same thing.
      */}
      {!isDegraded && streamError && (
        <div
          role="alert"
          data-testid="observability-log-stream-error"
          className="px-3 py-2 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-[12px] text-red-800 dark:text-red-200"
        >
          {streamError}
        </div>
      )}

      {/* Terminal surface */}
      <div className="flex-1 min-h-0 relative bg-[#0a0a0a]">
        <div ref={containerRef} className="absolute inset-0 p-2" />
        {/* Per-line mirror for QA / a11y. Sized to a 1x1 sr-only box so it
            doesn't disrupt the visual layout but Playwright can locate each
            line via data-testid="observability-log-line". */}
        <ul
          aria-hidden="true"
          className="sr-only"
          data-testid="observability-log-lines"
        >
          {visibleLines.map((entry, idx) => (
            <li
              key={`${entry.ts}-${idx}`}
              data-testid="observability-log-line"
              data-stream={entry.stream}
            >
              {entry.line}
            </li>
          ))}
        </ul>
        {/* "Resume tail" floats over the bottom-right when the user has
            scrolled up — explicit affordance to drop them back at live. */}
        {!followTail && (
          <button
            type="button"
            onClick={handleResumeTail}
            data-testid="observability-log-resume-tail"
            className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md"
          >
            <ArrowDown className="h-3 w-3" strokeWidth={1.75} />
            Resume tail
          </button>
        )}
      </div>

      {/* Download error (non-blocking, dismissible by retry) */}
      {downloadError && (
        <div
          className="px-3 py-1.5 text-[11px] text-red-600 dark:text-red-300 border-t border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20"
          data-testid="observability-download-error"
        >
          {downloadError}
        </div>
      )}
    </div>
  )
}

export default LogTerminal
